import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { AuthContext } from '../context/AuthContext';
import {
  getUser,
  getUserPosts,
  followUser,
  unfollowUser,
  checkFollowing,
  getMedia,
  getMediaUrl,
  getFullProfile,
  uploadFile,
  updateProfile,
  getProfessions,
  addProfession,
  deleteProfession,
  getMutualConnections,
} from '../services/api';
import Post from '../components/Post';
import AvailabilityTimeline from '../components/AvailabilityTimeline';
import '../components/AvailabilityTimeline.css';
import PhotoPositioner from '../components/PhotoPositioner';
import { colors } from '../styles/theme';
import { hasShowcaseContent } from '../utils/showcaseStatus';
import './Profile.css';

const emptyFollowStatus = { isFollowing: false, isMutual: false };

const fetchProfileView = async ([, userId, isOwnProfile]) => {
  const [profileRes, postsRes, mediaRes, fullProfileRes, professionsRes, followingRes, mutualRes] =
    await Promise.all([
      getUser(userId),
      getUserPosts(userId).catch((error) => {
        console.error('Error loading posts:', error);
        return { data: [] };
      }),
      getMedia(userId).catch(() => ({ data: [] })),
      getFullProfile(userId).catch(() => ({ data: {} })),
      getProfessions(userId).catch(() => ({ data: [] })),
      isOwnProfile
        ? Promise.resolve({ data: emptyFollowStatus })
        : checkFollowing(userId).catch((error) => {
            console.error('Error checking follow status:', error);
            return { data: emptyFollowStatus };
          }),
      isOwnProfile
        ? Promise.resolve({ data: [] })
        : getMutualConnections(userId).catch(() => ({ data: [] })),
    ]);

  const media = mediaRes.data || [];
  const headshot = media.find((m) => m.media_type === 'image' && m.slot === 'headshot');
  const anyImage = media.find((m) => m.media_type === 'image');
  const fullData = fullProfileRes.data || {};
  const mutuals = mutualRes.data || [];

  return {
    profile: profileRes.data,
    posts: postsRes.data || [],
    heroPhoto: headshot || anyImage || null,
    professions: Array.isArray(professionsRes.data) ? professionsRes.data : [],
    hasShowcase: hasShowcaseContent(fullData),
    followStatus: followingRes.data || emptyFollowStatus,
    mutualCount: Array.isArray(mutuals) ? mutuals.length : 0,
  };
};

function Profile() {
  const { userId } = useParams();
  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isMutual, setIsMutual] = useState(false);
  const [heroPhoto, setHeroPhoto] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [professions, setProfessions] = useState([]);
  const [hasShowcase, setHasShowcase] = useState(true);
  const [repositioning, setRepositioning] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', headline: '', location: '' });
  const [newProfession, setNewProfession] = useState('');
  const [mutualCount, setMutualCount] = useState(0);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectMessage, setConnectMessage] = useState('');
  const [connectMedia, setConnectMedia] = useState(null);
  const [connectMediaType, setConnectMediaType] = useState(null);
  const avatarInputRef = useRef(null);
  const connectModalRef = useRef(null);
  const connectPhotoRef = useRef(null);
  const connectVideoRef = useRef(null);

  const isOwnProfile = currentUser && currentUser.id === parseInt(userId);

  const {
    data: profileView,
    error: profileViewError,
    isLoading,
    mutate: refreshProfileView,
  } = useSWR(userId ? ['profile/view', userId, Boolean(isOwnProfile)] : null, fetchProfileView);

  useEffect(() => {
    if (!profileView) return;
    setProfile(profileView.profile);
    setPosts(profileView.posts);
    setHeroPhoto(profileView.heroPhoto);
    setProfessions(profileView.professions);
    setHasShowcase(profileView.hasShowcase);
    setIsFollowing(profileView.followStatus.isFollowing);
    setIsMutual(profileView.followStatus.isMutual || false);
    setMutualCount(profileView.mutualCount);
  }, [profileView]);

  useEffect(() => {
    if (!profileViewError) return;
    console.error('Error loading profile:', profileViewError);
  }, [profileViewError]);

  const handleFollow = async () => {
    if (isFollowing) {
      try {
        await unfollowUser(userId);
        setIsFollowing(false);
        setIsMutual(false);
        refreshProfileView();
      } catch (error) {
        console.error('Error unfollowing:', error);
      }
    } else {
      setShowConnectModal(true);
    }
  };

  const handleSendConnection = async () => {
    try {
      // Upload media if a file was selected
      let mediaUrl = null;
      if (connectMedia && connectPhotoRef.current?.files?.[0]) {
        const uploadRes = await uploadFile(connectPhotoRef.current.files[0]);
        mediaUrl = uploadRes.data?.url;
      } else if (connectMedia && connectVideoRef.current?.files?.[0]) {
        const uploadRes = await uploadFile(connectVideoRef.current.files[0]);
        mediaUrl = uploadRes.data?.url;
      }

      let message = connectMessage.trim() || undefined;
      if (mediaUrl) {
        const mediaTag =
          connectMediaType === 'image' ? `[image:${mediaUrl}]` : `[video:${mediaUrl}]`;
        message = message ? `${message}\n${mediaTag}` : mediaTag;
      }

      await followUser(userId, message);
      setIsFollowing(true);
      setShowConnectModal(false);
      setConnectMessage('');
      setConnectMedia(null);
      setConnectMediaType(null);
      refreshProfileView();
    } catch (error) {
      console.error('Error following:', error);
    }
  };

  const closeConnectModal = () => {
    setShowConnectModal(false);
    setConnectMessage('');
    setConnectMedia(null);
    setConnectMediaType(null);
  };

  const handleConnectPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setConnectMedia(event.target.result);
        setConnectMediaType('image');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConnectVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setConnectMedia(event.target.result);
        setConnectMediaType('video');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddProfession = async () => {
    if (!newProfession.trim()) return;
    try {
      const res = await addProfession({ profession: newProfession.trim() });
      const id = res.data?.id;
      setProfessions((prev) => [...prev, { profession: newProfession.trim(), id }]);
      setNewProfession('');
    } catch (e) {
      console.error('Error adding profession:', e);
    }
  };

  const handleDeleteProfession = async (id) => {
    try {
      await deleteProfession(id);
      setProfessions((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error('Error deleting profession:', e);
    }
  };

  const openEditProfile = () => {
    setEditForm({
      full_name: profile.full_name || '',
      headline: profile.headline || '',
      bio: profile.bio || '',
      location: profile.location || '',
    });
    setEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile(userId, { ...profile, ...editForm });
      setProfile((prev) => ({ ...prev, ...editForm }));
      setEditingProfile(false);
    } catch (err) {
      console.error('Error saving profile:', err);
    }
  };

  const handleMessage = () => {
    navigate(`/messages/${userId}`);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const uploadRes = await uploadFile(file);
      const imageUrl = uploadRes.data.url;
      await updateProfile(userId, {
        ...profile,
        profile_image: imageUrl,
        profile_image_x: 50,
        profile_image_y: 50,
        profile_image_zoom: 100,
      });
      setProfile((prev) => ({
        ...prev,
        profile_image: imageUrl,
        profile_image_x: 50,
        profile_image_y: 50,
        profile_image_zoom: 100,
      }));
      setRepositioning(true);
    } catch (err) {
      console.error('Error uploading profile picture:', err);
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleAvatarPositionSave = async (x, y, zoom) => {
    try {
      await updateProfile(userId, {
        ...profile,
        profile_image_x: x,
        profile_image_y: y,
        profile_image_zoom: zoom,
      });
      setProfile((prev) => ({
        ...prev,
        profile_image_x: x,
        profile_image_y: y,
        profile_image_zoom: zoom,
      }));
    } catch (err) {
      console.error('Error saving position:', err);
    }
    setRepositioning(false);
  };

  if (isLoading && !profile) {
    // REVIEW: theme tokens
    return (
      <div className="profile-page">
        <div
          style={{
            padding: '80px',
            textAlign: 'center',
            fontFamily: "'Rethink Sans', sans-serif",
            color: colors.textSecondary,
          }}
        >
          Loading profile...
        </div>
      </div>
    );
  }

  if (!profile) {
    // REVIEW: theme tokens
    return (
      <div className="profile-page">
        <div
          style={{
            padding: '80px',
            textAlign: 'center',
            fontFamily: "'Rethink Sans', sans-serif",
            color: colors.textSecondary,
          }}
        >
          Profile not found
        </div>
      </div>
    );
  }

  const displayName = profile.full_name || profile.username;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="profile-page">
      {/* Connect Modal */}
      {showConnectModal && (
        <div className="connect-modal-overlay" onClick={closeConnectModal}>
          <div className="connect-modal" ref={connectModalRef} onClick={(e) => e.stopPropagation()}>
            <div className="connect-modal-header">
              <div className="connect-modal-person">
                <div className="connect-modal-avatar">{displayName.charAt(0).toUpperCase()}</div>
                <div className="connect-modal-person-info">
                  <div className="connect-modal-name">{displayName}</div>
                  <div className="connect-modal-title">
                    {professions[0]?.title || profile.industry || ''}
                  </div>
                </div>
              </div>
              <button className="connect-modal-close" onClick={closeConnectModal}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="connect-modal-message">
              <label className="connect-modal-label">Add a message (optional)</label>
              <textarea
                className="connect-modal-textarea"
                placeholder="Hi! I'd love to connect with you..."
                value={connectMessage}
                onChange={(e) => setConnectMessage(e.target.value)}
              />
            </div>

            <div className="connect-modal-media">
              <label className="connect-modal-label">Attach media (optional)</label>
              <div className="connect-modal-media-btns">
                <button
                  className="connect-modal-media-btn"
                  onClick={() => connectPhotoRef.current?.click()}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  Photo
                </button>
                <button
                  className="connect-modal-media-btn"
                  onClick={() => connectVideoRef.current?.click()}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polygon points="23 7 16 12 23 17 23 7"></polygon>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                  </svg>
                  Video
                </button>
              </div>
              <input
                type="file"
                ref={connectPhotoRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleConnectPhotoUpload}
              />
              <input
                type="file"
                ref={connectVideoRef}
                accept="video/*"
                style={{ display: 'none' }}
                onChange={handleConnectVideoUpload}
              />

              {connectMedia && (
                <div className="connect-modal-preview">
                  {connectMediaType === 'image' ? (
                    <img src={connectMedia} alt="Preview" className="connect-modal-preview-img" />
                  ) : (
                    <video src={connectMedia} className="connect-modal-preview-video" controls />
                  )}
                  <button
                    className="connect-modal-preview-remove"
                    onClick={() => {
                      setConnectMedia(null);
                      setConnectMediaType(null);
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="connect-modal-actions">
              <button className="connect-modal-cancel" onClick={closeConnectModal}>
                Cancel
              </button>
              <button className="connect-modal-send" onClick={handleSendConnection}>
                Send Connection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {editingProfile && (
        <div className="profile-edit-overlay" onClick={() => setEditingProfile(false)}>
          <div className="profile-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-edit-modal-header">
              <h2>Edit Profile</h2>
              <button className="profile-edit-modal-close" onClick={() => setEditingProfile(false)}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="profile-edit-modal-body">
              <div className="profile-edit-field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Your full name"
                />
              </div>

              <div className="profile-edit-field">
                <label>Headline</label>
                <input
                  type="text"
                  value={editForm.headline}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, headline: e.target.value }))}
                  placeholder="e.g. Hamilton on Broadway | BFA NYU Tisch | AEA"
                />
                <span className="profile-edit-hint">
                  This shows under your name. Include your top credits, school, or union status.
                </span>
              </div>

              <div className="profile-edit-field">
                <label>Location</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g. New York, NY"
                />
              </div>

              <div className="profile-edit-field">
                <label>Professions</label>
                <div className="profile-edit-professions">
                  {professions.map((p) => (
                    <span key={p.id} className="profile-edit-profession-tag">
                      {p.profession}
                      <button onClick={() => handleDeleteProfession(p.id)}>×</button>
                    </span>
                  ))}
                </div>
                <div className="profile-edit-profession-add">
                  <input
                    type="text"
                    value={newProfession}
                    onChange={(e) => setNewProfession(e.target.value)}
                    placeholder="Add a profession..."
                    onKeyDown={(e) =>
                      e.key === 'Enter' && (e.preventDefault(), handleAddProfession())
                    }
                  />
                  {newProfession.trim() && <button onClick={handleAddProfession}>Add</button>}
                </div>
              </div>

              <div className="profile-edit-field">
                <label>Profile Photo</label>
                <button
                  className="profile-edit-photo-btn"
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {uploadingAvatar ? 'Uploading...' : 'Change Photo'}
                </button>
              </div>
            </div>

            <div className="profile-edit-modal-footer">
              <button className="profile-edit-cancel" onClick={() => setEditingProfile(false)}>
                Cancel
              </button>
              <button className="profile-edit-save" onClick={handleSaveProfile}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner */}
      <div className="profile-banner">
        <div className="profile-banner-gradient" />
      </div>

      {/* Profile + Showcase Split */}
      <div className="profile-hero-split">
        {/* Left: Profile Info Card */}
        <div className="profile-info-card">
          <div className="profile-avatar-wrap">
            <div className="profile-hero-avatar">
              {profile.profile_image ? (
                <img
                  src={getMediaUrl(profile.profile_image)}
                  alt={displayName}
                  style={{
                    objectPosition: `${profile.profile_image_x ?? 50}% ${profile.profile_image_y ?? 50}%`,
                    transform:
                      (profile.profile_image_zoom ?? 100) > 100
                        ? `scale(${(profile.profile_image_zoom ?? 100) / 100})`
                        : undefined,
                    transformOrigin: `${profile.profile_image_x ?? 50}% ${profile.profile_image_y ?? 50}%`,
                  }}
                />
              ) : (
                initial
              )}
              {isOwnProfile && (
                <div className="profile-hero-avatar-overlay">
                  <button onClick={() => avatarInputRef.current?.click()} title="Upload photo">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </button>
                </div>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarUpload}
              />
            </div>
          </div>

          {isOwnProfile && (
            <button
              className="profile-edit-section-btn"
              title="Edit profile"
              onClick={openEditProfile}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}

          <div className="profile-info-body">
            <h1 className="profile-hero-name">{displayName}</h1>

            {profile.headline ? (
              <p className="profile-hero-headline">{profile.headline}</p>
            ) : isOwnProfile ? (
              <p
                className="profile-hero-headline profile-hero-headline-empty"
                onClick={openEditProfile}
              >
                Add a headline
              </p>
            ) : null}

            {profile.location && <p className="profile-hero-location">{profile.location}</p>}

            <p className="profile-hero-connections">
              <span className="profile-connections-count">
                {profile.followers || 0} connection{(profile.followers || 0) !== 1 ? 's' : ''}
              </span>
              {!isOwnProfile && mutualCount > 0 && (
                <span className="profile-mutual-count"> · {mutualCount} mutual</span>
              )}
            </p>

            {professions.length > 0 && (
              <div className="profile-profession-tags">
                {professions.map((p, i) => (
                  <span key={i} className="profile-profession-tag">
                    {p.profession}
                  </span>
                ))}
              </div>
            )}

            <div className="profile-hero-actions">
              {!isOwnProfile ? (
                <>
                  <button
                    className={`profile-hero-btn profile-hero-btn-primary${isFollowing ? ' following' : ''}`}
                    onClick={handleFollow}
                  >
                    {isFollowing ? 'Connected' : 'Connect'}
                  </button>
                  {isMutual ? (
                    <button
                      className="profile-hero-btn profile-hero-btn-secondary"
                      onClick={handleMessage}
                    >
                      Message
                    </button>
                  ) : (
                    <button
                      className="profile-hero-btn profile-hero-btn-secondary"
                      disabled
                      title="Connect to message"
                    >
                      Connect to Message
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    className="profile-hero-btn profile-hero-btn-edit"
                    onClick={openEditProfile}
                  >
                    Edit Profile
                  </button>
                  <Link
                    to="/profile-settings"
                    className="profile-hero-btn profile-hero-btn-secondary"
                  >
                    Edit Showcase
                  </Link>
                  {profile.slug && (
                    <Link
                      to={`/${profile.slug}`}
                      className="profile-hero-btn profile-hero-btn-showcase-link"
                    >
                      {window.location.host}/{profile.slug}
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right: Showcase Preview */}
        <Link
          to={
            hasShowcase
              ? profile.slug
                ? `/${profile.slug}`
                : `/showcase/user/${userId}`
              : isOwnProfile
                ? '/profile-settings'
                : '#'
          }
          className={`profile-showcase-card ${!hasShowcase ? 'empty' : ''}`}
        >
          {hasShowcase && heroPhoto ? (
            <>
              <img
                src={getMediaUrl(heroPhoto.url)}
                alt=""
                className="profile-showcase-card-img"
                style={{
                  objectPosition: `${heroPhoto.position_x ?? 50}% ${heroPhoto.position_y ?? 50}%`,
                }}
              />
              <div className="profile-showcase-card-overlay">
                <span className="profile-showcase-card-label">{displayName}'s Showcase</span>
                <span className="profile-showcase-card-cta">View Portfolio →</span>
              </div>
            </>
          ) : hasShowcase ? (
            <div className="profile-showcase-card-overlay">
              <span className="profile-showcase-card-label">{displayName}'s Showcase</span>
              <span className="profile-showcase-card-cta">View Portfolio →</span>
            </div>
          ) : (
            <div className="profile-showcase-card-empty-content">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
              <span>{isOwnProfile ? 'Set up your Showcase' : 'No showcase yet'}</span>
            </div>
          )}
        </Link>
      </div>

      {/* Availability */}
      <div className="profile-availability-section">
        <div className="profile-availability-card">
          <AvailabilityTimeline userId={userId} isOwnProfile={isOwnProfile} />
        </div>
      </div>

      {/* Posts */}
      <div className="profile-posts-section">
        <div className="profile-posts-inner">
          <div className="profile-posts-header">
            <div>
              <span className="profile-posts-title">Activity</span>
            </div>
            <div className="profile-posts-header-right">
              {isOwnProfile && (
                <Link to="/feed" className="profile-create-post-btn">
                  Create a post
                </Link>
              )}
              <span className="profile-posts-count">
                {posts.length} post{posts.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          {posts.length === 0 ? (
            <div className="profile-posts-empty">
              <p>{isOwnProfile ? "You haven't posted yet" : 'No posts yet'}</p>
            </div>
          ) : (
            <div className="profile-posts-carousel">
              {posts.map((post) => (
                <div key={post.id} className="profile-post-card">
                  <Post post={post} onDelete={refreshProfileView} onUpdate={refreshProfileView} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Photo Repositioner Modal */}
      {repositioning && profile.profile_image && (
        <div className="profile-reposition-modal">
          <div className="profile-reposition-content">
            <PhotoPositioner
              photoUrl={getMediaUrl(profile.profile_image)}
              positionX={profile.profile_image_x ?? 50}
              positionY={profile.profile_image_y ?? 50}
              zoom={profile.profile_image_zoom ?? 100}
              aspectRatio="1/1"
              onSave={handleAvatarPositionSave}
              onCancel={() => setRepositioning(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
